import { z } from "zod";
import { slugString } from "./slug.js";

// A user-saved, NAMED, ordered set of exercises — a reusable superset
// "template". Born when the athlete ⭐-marks a superset in the logger; later
// inserted into a workout in one tap (all members added + grouped, no
// add-one-by-one). References catalog slugs only (no name snapshots — the
// display name resolves at render, [[feedback_no_denormalized_display_snapshots]]).
//
// Stored pk=USER#<sub>, sk=EXGROUP#<13-ms createdAt>#<id> — a per-user
// time-series, newest-first like Measurement, so the template shelf shows the
// most recently saved groups first. NOT on the set-aggregation path (additive
// entity, like WorkoutNote) → zero training-data risk.

const ExerciseSlug = slugString(64);

// A superset is ≥2 exercises; 12 is a generous circuit ceiling. The name is a
// short, user-editable label (the default is an auto-abbreviation of the
// members, computed client-side — see mobile supersetAbbrev).
const GroupName = z.string().trim().min(1).max(60);
const GroupSlugs = z.array(ExerciseSlug).min(2).max(12);

export const ExerciseGroup = z.object({
  id: z.uuid(),
  // Partition-key data, included so a single-row read is self-describing.
  userSub: z.string().min(1).max(128),
  name: GroupName,
  exerciseSlugs: GroupSlugs,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ExerciseGroup = z.infer<typeof ExerciseGroup>;

// Client → server. id optional but a UUID when present (offline-first
// idempotency, same pattern as NewMeasurement / NewWorkoutNote).
export const NewExerciseGroup = ExerciseGroup.omit({
  id: true,
  userSub: true,
  createdAt: true,
  updatedAt: true,
}).extend({ id: z.uuid().optional() });
export type NewExerciseGroup = z.infer<typeof NewExerciseGroup>;

// Update payload — name + membership are mutable (rename / reorder / swap a
// member). createdAt is the sk component and stays immutable.
export const PatchExerciseGroup = z.object({
  name: GroupName.optional(),
  exerciseSlugs: GroupSlugs.optional(),
});
export type PatchExerciseGroup = z.infer<typeof PatchExerciseGroup>;
